import { Paper, Typography } from '@material-ui/core';
import BNSChips from 'Components/AdvisorApp/Viewport/ClientExperience/ClientExperienceAccordionList/BNSChips';
import MetaCollectionFormInline from 'Components/UI/Meta/MetaForm/MetaCollectionFormInline';
import AccomplishmentLayout
  from 'Components/AdvisorApp/Viewport/ClientExperience/ClientExperienceAccordionList/AccomplishmentForm';
import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  buildUseStyles,
  useTheme,
} from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import { useSnackbarContext } from 'Context/SnackbarContext';

const useStyles = buildUseStyles('MessagesPageContent', (theme) => ({
  root: {
    padding: theme.spacing(2),
    borderRadius: theme.radius.sm,
  },
  accomplishmentForm: {
    padding: 0,
  },
  forms: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    '& > h3': {
      fontWeight: theme.typography.fontWeightBold,
    },
  },
}));

const Accomplishments = ({
  className,
  isFutureAccomplishment,
  bestNextStepLink,
  accomplishmentsLink,
  title,
  text,
}) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const { displayErrorMessage } = useSnackbarContext();
  const [bestNextSteps, setBestNextSteps] = React.useState();
  const [accomplishments, setAccomplishments] = React.useState();
  const [numAccomplishments, setNumAccomplishments] = React.useState(0);
  const { logEvent } = useEventTrackingLogEvent({
    nodeName: 'Accomplishments',
  });

  async function fetchAccomplishments() {
    const current = await accomplishmentsLink.get();
    setAccomplishments(current);
  }
  async function fetchBestNextSteps() {
    const nbnsBns = await bestNextStepLink.get();
    setBestNextSteps(nbnsBns);
  }

  React.useEffect(() => {
    fetchAccomplishments()
      .catch(e => displayErrorMessage(e,
        'Failed to fetch accomplishments'));
    fetchBestNextSteps()
      .catch(e => displayErrorMessage(e,
        'Failed to fetch recommended tags for best next steps'));
  }, []);

  return (
    <Paper className={classNames(classes.root, className)}>
      <div className={classes.title}>
        <Typography variant="h3" noWrap>
          {title}
        </Typography>
          &nbsp;
        <Typography variant="h6" component="p" noWrap>
          {text}
        </Typography>
      </div>
      <div>
        {(accomplishments && bestNextSteps) && <BNSChips
          onClick={(e, bns) => {
            accomplishments.create({
              useCollectionMetaOverrideSet: true,
              addToCollection: true,
            });
            const accomplishmentResources = accomplishments.resources().list();
            const index = accomplishmentResources.length - 1;
            accomplishments
              .resources()
              .index(index)
              .attr('type', bns.attr('bestNextStepName'));
            setAccomplishments(accomplishments);
          }}
          bestNextSteps={bestNextSteps}
        />}
      </div>
      {accomplishments && <MetaCollectionFormInline
        classes={{
          forms: classes.forms,
        }}
        collection={accomplishments}
        addButtonText="Add Accomplishment"
        AddButtonProps={{
          test: isFutureAccomplishment ? 'add-future-accomplishment-button' : 'add-accomplishment-button',
        }}
        FormProps={{
          className: classes.accomplishmentForm,
          layout: AccomplishmentLayout,
          onSubmit: async (collection, idx) => {
            if (collection) {
              const [next] = collection;
              const added = accomplishments.copy();
              added
                .resources()
                .replaceAtIndex(Number(idx), next);
              setAccomplishments(added);
              const nextCollection = await accomplishments.link('self').get();
              setAccomplishments(nextCollection);
              logEvent('Created Accomplishment', {
                accomplishmentType: next.attr('type'),
              });
            }

            return collection;
          },
          LayoutProps: {
            onRemove: async (r, idx) => {
              async function removeAccomplishment() {
                if (r.link('self')) {
                  await r.delete();
                  fetchAccomplishments();
                  const nextCollection = await accomplishments.link('self').get();
                  setAccomplishments(nextCollection);
                } else {
                  const removed = accomplishments.copy();
                  removed
                    .resources()
                    .removeItemByIndex(idx);
                  setAccomplishments(removed);
                  setNumAccomplishments(numAccomplishments - 1);
                }
              }

              removeAccomplishment().catch((e) => displayErrorMessage(e,
                'Failed to remove accomplishment.'));
            },
            isFutureAccomplishment,
          },
          inlineSubmit: true,
        }}
        onAdd={() => {
          accomplishments.create({
            useCollectionMetaOverrideSet: true,
            addToCollection: true,
          });
          setNumAccomplishments(numAccomplishments + 1);
        }}
      />}
    </Paper>
  );
};

Accomplishments.defaultProps = {
  className: '',
};

Accomplishments.propTypes = {
  className: PropTypes.string,
  isFutureAccomplishment: PropTypes.bool.isRequired,
  bestNextStepLink: PropTypes.object.isRequired,
  accomplishmentsLink: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
};

export { Accomplishments };
